"use client";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  selectAboutResponse,
  aboutSaveDetailsAsync,
  fetchCitiesAsync,
  fetchCountryAsync,
  fetchStateAsync,
  selectFetchCities,
  selectFetchCountry,
  selectFetchState,
} from "./AboutSlice";
import { Box, Button, useToast, Spinner } from "@chakra-ui/react";
import { ArrowRightIcon } from "@chakra-ui/icons";
import {
  fetchBasicDetailsAsync,
  selectBasicDetails,
} from "../../../profile/BasicDetailsController/BasicDetailsSlice";

const AboutYou = () => {
  const {
    handleSubmit,
    watch,
    setValue,
    register,
    formState: { errors, isSubmitting },
  } = useForm({
    mode: "onBlur", // Validate on blur for better UX
    defaultValues: {
      country_uuid: "ecd3d725-a577-11ef-806e-89fb4f970b6e",
      state_uuid: "",
      city_uuid: "",
      active_job_search: "",
      work_status: "",
      marital_status: "",
      gender: "",
    },
  });

  // useSelectors
  const countryList = useSelector(selectFetchCountry);
  const stateList = useSelector(selectFetchState);
  const citiesList = useSelector(selectFetchCities);
  const navigate = useNavigate();
  const indiaUUID = "ecd3d725-a577-11ef-806e-89fb4f970b6e";

  // State variables
  const [selectedCountryId, setSelectedCountryId] = useState(indiaUUID);
  const [selectedStateId, setSelectedStateId] = useState("");
  const [charCount, setCharCount] = useState(0);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);

  const dispatch = useDispatch();
  const toast = useToast();
  const basicDetails = useSelector(selectBasicDetails);
  const basicsCheck = basicDetails?.basic_details?.uuid;
  const [isChecking, setIsChecking] = useState(true);

  // Check basic details on mount
  useEffect(() => {
    const checkBasicDetails = async () => {
      try {
        await dispatch(fetchBasicDetailsAsync());
      } catch (error) {
        console.error("Error fetching basic details:", error);
      } finally {
        setIsChecking(false);
      }
    };

    checkBasicDetails();
  }, [dispatch]);

  // Redirect if basic details exist
  useEffect(() => {
    if (!isChecking && basicsCheck) {
      navigate("/candidate-dashboard");
    }
  }, [basicsCheck, isChecking, navigate]);

  // Fetch country list on mount
  useEffect(() => {
    const loadCountries = async () => {
      try {
        if (!countryList || countryList.length === 0) {
          await dispatch(fetchCountryAsync());
        }
      } catch (error) {
        toast({
          title: "Error loading countries",
          description: "Failed to load country list. Please refresh the page.",
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }
    };

    loadCountries();
  }, [dispatch]);

  // Fetch states when country changes
  useEffect(() => {
    const loadStates = async () => {
      if (selectedCountryId) {
        setIsLoadingStates(true);
        setSelectedStateId(""); // Reset state selection
        setValue("state_uuid", "");
        setValue("city_uuid", "");

        try {
          await dispatch(fetchStateAsync(selectedCountryId));
        } catch (error) {
          toast({
            title: "Error loading states",
            description: "Failed to load state list. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          });
        } finally {
          setIsLoadingStates(false);
        }
      }
    };

    loadStates();
  }, [selectedCountryId, dispatch, setValue]);

  // Fetch cities when state changes
  useEffect(() => {
    const loadCities = async () => {
      if (selectedStateId) {
        setIsLoadingCities(true);
        setValue("city_uuid", ""); // Reset city selection

        try {
          await dispatch(fetchCitiesAsync(selectedStateId));
        } catch (error) {
          toast({
            title: "Error loading cities",
            description: "Failed to load city list. Please try again.",
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top-right",
          });
        } finally {
          setIsLoadingCities(false);
        }
      }
    };

    loadCities();
  }, [selectedStateId, dispatch, setValue]);

  if (isChecking) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Spinner size="xl" color="blue.500" />
      </div>
    );
  }

  const onSubmit = async (data) => {
    try {
      const response = await dispatch(aboutSaveDetailsAsync({ data: data }));
      const selectedValue = data.work_status === "Yes";

      toast({
        title: "Details saved",
        description: "Your profile information has been successfully saved.",
        status: "success",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });

      localStorage.setItem("isCurrentlyWorking", selectedValue);
      navigate("/candidate-profile/education");
    } catch (error) {
      toast({
        title: "Error saving details",
        description:
          error.message ||
          "There was an error saving your information. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    }
  };

  const handleAboutYouChange = (e) => {
    const text = e.target.value;
    setCharCount(text.length);
  };

  return (
    <div className="w-full">
      <div className="h-screen flex flex-col pt-10">
        <div className="mb-3">
          <h4>About You</h4>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="flex gap-3">
            <div className="flex w-1/2 flex-col mb-3">
              <label>
                Date Of Birth <span className="text-red-500">*</span>
              </label>
              <input
                className="border rounded-lg border-gray-100 my-2 px-3 py-2 text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register("dob", {
                  required: "Date of birth is required",
                  validate: (value) => {
                    const dob = new Date(value);
                    const today = new Date();
                    const age = today.getFullYear() - dob.getFullYear();
                    const monthDiff = today.getMonth() - dob.getMonth();
                    const dayDiff = today.getDate() - dob.getDate();

                    const isValidAge =
                      age > 18 ||
                      (age === 18 && monthDiff > 0) ||
                      (age === 18 && monthDiff === 0 && dayDiff >= 0);

                    return isValidAge || "You must be at least 18 years old";
                  },
                })}
                type="date"
                max={new Date(new Date().setFullYear(new Date().getFullYear() - 14)).toISOString().split('T')[0]}
                autoFocus
              />
              {errors.dob && (
                <p className="mt-1.5 text-sm text-red-600">
                  {errors.dob.message}
                </p>
              )}
            </div>

            <div className="flex w-1/2 flex-col mb-3">
              <label>
                Address <span className="text-red-500">*</span>
              </label>
              <input
                className="border rounded-lg border-gray-100 my-2 px-3 py-2 text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register("address", {
                  required: "Address is required",
                  minLength: {
                    value: 10,
                    message: "Address must be at least 10 characters",
                  },
                })}
                type="text"
                placeholder="Enter your complete address"
              />
              {errors.address && (
                <p className="mt-1.5 text-sm text-red-600">
                  {errors.address.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex w-1/2 flex-col mb-3">
              <label>
                Country <span className="text-red-500">*</span>
              </label>
              <select
                className="border rounded-lg border-gray-100 my-2 text-gray-500 h-10 px-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register("country_uuid", {
                  required: "Country is required",
                })}
                value={selectedCountryId}
                onChange={(e) => {
                  const newCountryId = e.target.value;
                  setSelectedCountryId(newCountryId);
                  setValue("country_uuid", newCountryId);
                }}
                disabled={!countryList || countryList.length === 0}
              >
                {countryList && countryList.length > 0 ? (
                  countryList.map((item) => (
                    <option key={item.id} value={item.uuid}>
                      {item.name}
                    </option>
                  ))
                ) : (
                  <option value="">Loading countries...</option>
                )}
              </select>
              {errors.country_uuid && (
                <p className="mt-1.5 text-sm text-red-600">
                  {errors.country_uuid.message}
                </p>
              )}
            </div>

            <div className="flex w-1/2 flex-col mb-3">
              <label>
                State <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  className="border rounded-lg border-gray-100 my-2 text-gray-500 h-10 px-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  {...register("state_uuid", {
                    required: "State is required",
                  })}
                  onChange={(e) => {
                    const newStateId = e.target.value;
                    setSelectedStateId(newStateId);
                    setValue("state_uuid", newStateId);
                  }}
                  disabled={isLoadingStates || !stateList || stateList.length === 0}
                >
                  <option value="">Select a state</option>
                  {stateList && stateList.length > 0 &&
                    stateList.map((state) => (
                      <option key={state.id} value={state.uuid}>
                        {state.name}
                      </option>
                    ))}
                </select>
                {isLoadingStates && (
                  <Spinner
                    size="sm"
                    className="absolute right-8 top-1/2 transform -translate-y-1/2"
                  />
                )}
              </div>
              {errors.state_uuid && (
                <p className="mt-1.5 text-sm text-red-600">
                  {errors.state_uuid.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex w-1/2 flex-col mb-3">
              <label>
                City <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  className="border rounded-lg border-gray-100 my-2 text-gray-500 h-10 px-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                  {...register("city_uuid", {
                    required: "City is required",
                  })}
                  disabled={
                    !selectedStateId ||
                    isLoadingCities ||
                    !citiesList ||
                    citiesList.length === 0
                  }
                >
                  <option value="">
                    {!selectedStateId
                      ? "Select a state first"
                      : "Select a city"}
                  </option>
                  {Array.isArray(citiesList) &&
                    citiesList.length > 0 &&
                    citiesList.map((city) => (
                      <option key={city.id} value={city.uuid}>
                        {city.name}
                      </option>
                    ))}
                </select>
                {isLoadingCities && (
                  <Spinner
                    size="sm"
                    className="absolute right-8 top-1/2 transform -translate-y-1/2"
                  />
                )}
              </div>
              {errors.city_uuid && (
                <p className="mt-1.5 text-sm text-red-600">
                  {errors.city_uuid.message}
                </p>
              )}
            </div>

            <div className="flex w-1/2 flex-col mb-3">
              <label>
                Pincode <span className="text-red-500">*</span>
              </label>
              <input
                className="border rounded-lg border-gray-100 my-2 px-3 py-2 text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register("pincode", {
                  required: "Pincode is required",
                  pattern: {
                    value: /^\d{6}$/,
                    message: "Pincode must be exactly 6 digits",
                  },
                })}
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="Enter 6-digit pincode"
              />
              {errors.pincode && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.pincode.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex w-1/2 flex-col mb-3">
              <label>
                Currently looking for a job{" "}
                <span className="text-red-500">*</span>
              </label>
              <select
                className="border rounded-lg border-gray-100 my-2 h-10 px-2 text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register("active_job_search", {
                  required: "This field is required",
                })}
              >
                <option value="">Select your status</option>
                <option value="true">Yes</option>
                <option value="false">No</option>
              </select>
              {errors.active_job_search && (
                <p className="mt-1.5 text-sm text-red-600">
                  {errors.active_job_search.message}
                </p>
              )}
            </div>

            <div className="flex w-1/2 flex-col mb-3">
              <label>
                Currently working <span className="text-red-500">*</span>
              </label>
              <select
                className="border rounded-lg border-gray-100 my-2 h-10 px-2 text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register("work_status", {
                  required: "This field is required",
                })}
              >
                <option value="">Select your status</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
              {errors.work_status && (
                <p className="mt-1.5 text-sm text-red-600">
                  {errors.work_status.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <div className="flex flex-col mb-3">
              <label>
                About you <span className="text-red-500">*</span>
                <span className="text-sm text-gray-500 ml-2">
                  ({charCount}/700 characters)
                </span>
              </label>
              <textarea
                className="border rounded-lg border-gray-100 my-2 px-3 py-2 text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register("profile_summery", {
                  required: "Please tell us about yourself",
                  minLength: {
                    value: 50,
                    message: "Please write at least 50 characters",
                  },
                  maxLength: {
                    value: 700,
                    message: "Maximum 600-700 characters allowed",
                  },
                })}
                maxLength={1000}
                rows={6}
                placeholder="Tell us about your professional background, skills, and career goals..."
                onChange={handleAboutYouChange}
              ></textarea>
              {errors.profile_summery && (
                <p className="mt-1.5 text-sm text-red-600">
                  {errors.profile_summery.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <div className="flex w-1/2 flex-col mb-3">
              <label>
                Marital Status <span className="text-red-500">*</span>
              </label>
              <select
                className="border rounded-lg border-gray-100 my-2 h-10 px-2 text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register("marital_status", {
                  required: "Marital status is required",
                })}
              >
                <option value="">Select your marital status</option>
                <option value="Single">Single</option>
                <option value="Married">Married</option>
                <option value="Divorced">Divorced</option>
                <option value="Widowed">Widowed</option>
              </select>
              {errors.marital_status && (
                <p className="mt-1.5 text-sm text-red-600">
                  {errors.marital_status.message}
                </p>
              )}
            </div>

            <div className="flex w-1/2 flex-col mb-3">
              <label>
                Gender <span className="text-red-500">*</span>
              </label>
              <select
                className="border rounded-lg border-gray-100 my-2 h-10 px-2 text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                {...register("gender", {
                  required: "Gender is required",
                })}
              >
                <option value="">Select your gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Transgender">Transgender</option>
              </select>
              {errors.gender && (
                <p className="mt-1.5 text-sm text-red-600">
                  {errors.gender.message}
                </p>
              )}
            </div>
          </div>

          <div className="w-full flex flex-wrap justify-center gap-2 mb-5">
            <Box textAlign="center" mt={4} width={{ base: "full", md: "auto" }}>
              <Button
                width={{
                  base: "full",
                  sm: "md",
                  md: "lg",
                  lg: "xl",
                  xl: "4xl",
                }}
                rightIcon={<ArrowRightIcon />}
                type="submit"
                colorScheme="blue"
                isLoading={isSubmitting}
                loadingText="Saving..."
                disabled={isSubmitting}
              >
                Next
              </Button>
            </Box>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AboutYou;
